import { EuroOutlined, MailOutlined, SettingsOutlined, TodayOutlined, VerifiedUserOutlined } from '@mui/icons-material';
import { DrawerContentConfig } from '../@types';
import { TFunction } from 'i18next';
import { EnumValues, Skin, SKIN } from '../types/enums';

const ACCOUNT_DRAWER_ITEM = {
  VERIFICATION: 'verification',
  BONUS: 'bonus',
  HISTORY: 'history',
  MESSAGES: 'messages',
  PREFERENCES: 'preferences',
} as const;

type AccountDrawerItemKey = EnumValues<typeof ACCOUNT_DRAWER_ITEM>;
type GetAccountDrawerConfigOptions = {
  includeVerification?: boolean;
  isCasinoOnlyProduct?: boolean;
  isSportsbookOnlyProduct?: boolean;
};

const CASINO_ONLY_HIDDEN_PATHS = new Set(['/my-account/bonus/sports', '/my-account/history/sports-betting']);
const SPORTSBOOK_ONLY_HIDDEN_PATHS = new Set(['/my-account/bonus/casino', '/my-account/history/casino']);

const drawerItemBuilders: Record<AccountDrawerItemKey, (t: TFunction) => DrawerContentConfig> = {
  [ACCOUNT_DRAWER_ITEM.VERIFICATION]: (t) => ({
    id: ACCOUNT_DRAWER_ITEM.VERIFICATION,
    icon: <VerifiedUserOutlined />,
    text: t('kyc.verification', 'Verification'),
    path: '/my-account/verification',
    children: [],
  }),
  [ACCOUNT_DRAWER_ITEM.BONUS]: (t) => ({
    id: ACCOUNT_DRAWER_ITEM.BONUS,
    icon: <EuroOutlined />,
    text: t('bonus'),
    children: [
      { text: t('sportsbook'), path: '/my-account/bonus/sports' },
      { text: t('casino'), path: '/my-account/bonus/casino' },
    ],
  }),
  [ACCOUNT_DRAWER_ITEM.HISTORY]: (t) => ({
    id: ACCOUNT_DRAWER_ITEM.HISTORY,
    icon: <TodayOutlined />,
    text: t('history'),
    children: [
      { text: t('sportsBetting'), path: '/my-account/history/sports-betting' },
      { text: t('casino'), path: '/my-account/history/casino' },
      { text: t('transactions'), path: '/my-account/history/transactions' },
    ],
  }),
  [ACCOUNT_DRAWER_ITEM.MESSAGES]: (t) => ({
    id: ACCOUNT_DRAWER_ITEM.MESSAGES,
    icon: <MailOutlined />,
    text: t('messages'),
    children: [
      { text: t('inbox'), path: '/my-account/messages/inbox' },
      { text: t('sent'), path: '/my-account/messages/sent' },
      { text: t('deleted'), path: '/my-account/messages/deleted' },
    ],
  }),
  [ACCOUNT_DRAWER_ITEM.PREFERENCES]: (t) => ({
    id: ACCOUNT_DRAWER_ITEM.PREFERENCES,
    icon: <SettingsOutlined />,
    text: t('preferences'),
    path: '/my-account/preferences',
    children: [],
  }),
};

const SKIN_DRAWER_ITEMS: Record<Skin, AccountDrawerItemKey[]> = {
  [SKIN.BET365]: [
    ACCOUNT_DRAWER_ITEM.VERIFICATION,
    ACCOUNT_DRAWER_ITEM.BONUS,
    ACCOUNT_DRAWER_ITEM.HISTORY,
    ACCOUNT_DRAWER_ITEM.MESSAGES,
    ACCOUNT_DRAWER_ITEM.PREFERENCES,
  ],
  [SKIN.GOLA]: [ACCOUNT_DRAWER_ITEM.MESSAGES],
  [SKIN.NEOBET]: [
    ACCOUNT_DRAWER_ITEM.BONUS,
    ACCOUNT_DRAWER_ITEM.HISTORY,
    ACCOUNT_DRAWER_ITEM.MESSAGES,
    ACCOUNT_DRAWER_ITEM.PREFERENCES,
  ],
};

const DEFAULT_ITEMS = SKIN_DRAWER_ITEMS[SKIN.BET365];

const normalizeDrawerItem = (
  item: DrawerContentConfig,
  { isCasinoOnlyProduct = false, isSportsbookOnlyProduct = false }: GetAccountDrawerConfigOptions
): DrawerContentConfig => {
  if ((!isCasinoOnlyProduct && !isSportsbookOnlyProduct) || item.children.length === 0) {
    return item;
  }

  const hiddenPaths = new Set<string>([
    ...(isCasinoOnlyProduct ? CASINO_ONLY_HIDDEN_PATHS : []),
    ...(isSportsbookOnlyProduct ? SPORTSBOOK_ONLY_HIDDEN_PATHS : []),
  ]);

  const children = item.children.filter((child) => !hiddenPaths.has(child.path));

  if (children.length === 1) {
    // In single-product mode, collapse single-option accordions into direct links
    // so the drawer does not show an expandable item with only one destination.
    return {
      ...item,
      path: children[0].path,
      children: [],
    };
  }

  return {
    ...item,
    children,
  };
};

export const getAccountDrawerConfig = (
  t: TFunction,
  skin: Skin,
  options?: GetAccountDrawerConfigOptions
): DrawerContentConfig[] => {
  const items = SKIN_DRAWER_ITEMS[skin] ?? DEFAULT_ITEMS;
  const filteredItems = items.filter((key) => key !== ACCOUNT_DRAWER_ITEM.VERIFICATION || options?.includeVerification);

  return filteredItems.map((key) => normalizeDrawerItem(drawerItemBuilders[key](t), options ?? {}));
};
